import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { PageHeader, StatusBadge } from "../../components/bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Mail, Trash2, Copy } from "lucide-react";

const ROLES = ["Sponsor", "Project Leader", "Mentor", "Team Member", "Reviewer", "Approver", "Contributor", "Observer", "Change Champion"];

export default function MembersPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [teamUsers, setTeamUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [addForm, setAddForm] = useState({ userId: "", role: "Team Member" });
  const [invForm, setInvForm] = useState({ email: "", role: "Team Member" });
  const [lastInvite, setLastInvite] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/projects/${projectId}/members`).then((r) => setMembers(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/projects/${projectId}/invites`).then((r) => setInvites(r.data)).catch(() => {});
    api.get("/team").then((r) => setTeamUsers(r.data)).catch(() => {});
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const addMember = async () => {
    try {
      await api.post(`/projects/${projectId}/members`, addForm);
      toast.success("Member added"); setAddOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sendInvite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/invites`, { ...invForm, originUrl: window.location.origin });
      setLastInvite(data);
      toast.success(data.emailSent ? "Invitation email queued via Service Bus" : "Invite created — share the link (email queue unavailable)");
      load();
    } catch (e) {
      toast.error(errMsg(e));
      if (e.response?.status === 402) navigate("/billing");
    }
    setBusy(false);
  };

  return (
    <div data-testid="members-page">
      <PageHeader overline="Collaboration · Role map drives escalations" title="Members & Roles"
        actions={
          <>
            <Button data-testid="invite-external-btn" variant="outline" className="rounded-none" onClick={() => { setLastInvite(null); setInvOpen(true); }}>
              <Mail className="h-4 w-4 mr-1" />Email Invite
            </Button>
            <Button data-testid="add-project-member-btn" className="rounded-none" onClick={() => setAddOpen(true)}>
              <Plus className="h-4 w-4 mr-1" />Add Member
            </Button>
          </>
        } />
      <div className="border border-border bg-card overflow-auto mb-6">
        <table className="w-full data-table text-sm">
          <thead><tr><th>Name</th><th>Email</th><th>Project Role</th><th></th></tr></thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} data-testid="project-member-row">
                <td className="font-medium">{m.name}</td>
                <td className="text-muted-foreground">{m.email}</td>
                <td><StatusBadge value={m.role} tone={["Sponsor", "Project Leader"].includes(m.role) ? "blue" : "neutral"} /></td>
                <td><button data-testid="remove-project-member-btn" onClick={async () => { await api.delete(`/projects/${projectId}/members/${m.id}`); load(); }} className="p-1 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button></td>
              </tr>
            ))}
            {members.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-muted-foreground">No members yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="overline-label mb-2">Pending invites</div>
      <div className="border border-border bg-card overflow-auto">
        <table className="w-full data-table text-sm">
          <thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Sent</th></tr></thead>
          <tbody>
            {invites.map((i) => (
              <tr key={i.id} data-testid="invite-row">
                <td>{i.email}</td><td><StatusBadge value={i.role} tone="neutral" /></td>
                <td><StatusBadge value={i.status} /></td>
                <td className="text-xs text-muted-foreground">{(i.createdAt || "").slice(0, 10)}</td>
              </tr>
            ))}
            {invites.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">No invites sent.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-display">Add Org User to Project</DialogTitle></DialogHeader>
          <Label>User</Label>
          <select data-testid="member-user-select" className="w-full border border-input px-3 py-2 text-sm bg-background" value={addForm.userId} onChange={(e) => setAddForm({ ...addForm, userId: e.target.value })}>
            <option value="">Select user…</option>
            {teamUsers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
          </select>
          <Label>Project role</Label>
          <select data-testid="member-role-select-dlg" className="w-full border border-input px-3 py-2 text-sm bg-background" value={addForm.role} onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <Button data-testid="save-project-member-btn" className="rounded-none" onClick={addMember} disabled={!addForm.userId}>Add to project</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent className="rounded-none max-w-md">
          <DialogHeader><DialogTitle className="font-display">Invite External Person</DialogTitle></DialogHeader>
          <Label>Email</Label>
          <Input data-testid="invite-email-input" type="email" value={invForm.email} onChange={(e) => setInvForm({ ...invForm, email: e.target.value })} />
          <Label>Project role</Label>
          <select data-testid="invite-role-select" className="w-full border border-input px-3 py-2 text-sm bg-background" value={invForm.role} onChange={(e) => setInvForm({ ...invForm, role: e.target.value })}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <Button data-testid="send-invite-btn" className="rounded-none bg-[#1E6FE8] hover:bg-[#1557C6]" onClick={sendInvite} disabled={busy || !invForm.email}>
            {busy ? "Sending…" : "Send invitation"}
          </Button>
          {lastInvite && (
            <div data-testid="invite-link-box" className="border border-border p-3 text-xs space-y-1">
              <div className="overline-label">Invite link (also emailed)</div>
              <div className="font-mono2 break-all">{lastInvite.inviteUrl}</div>
              <button className="underline text-[#1E6FE8]" onClick={() => { navigator.clipboard.writeText(lastInvite.inviteUrl); toast.success("Copied"); }}>
                <Copy className="h-3 w-3 inline mr-1" />Copy link
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
